import type { ReceiptDetail, UpdateReceiptParams } from '@/api/types/receipt'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'

import { formatFieldLabel } from '../field-labels'
import { toNumberOrNull } from '../field-value'

// crosscheck の field_name のうち、そのまま確定値に上書きできるもの。
// purchased_date / purchased_time や items[N].* は本体の1フィールドに対応しないため対象外。
const ADOPTABLE_FIELDS: Record<string, 'text' | 'number'> = {
  store_name: 'text',
  store_invoice_no: 'text',
  payment_method: 'text',
  subtotal: 'number',
  tax_amount: 'number',
  total_amount: 'number',
  discount_total: 'number',
  stated_item_count: 'number',
  paid_amount: 'number',
  change_amount: 'number',
  tax8_target: 'number',
  tax8_amount: 'number',
  tax10_target: 'number',
  tax10_amount: 'number',
}

const buildAdoptParams = (fieldName: string, value: string | null): UpdateReceiptParams | null => {
  const kind = ADOPTABLE_FIELDS[fieldName]
  if (!kind) return null
  if (kind === 'number') return { [fieldName]: toNumberOrNull(value ?? '') }
  return { [fieldName]: value }
}

type Props = {
  receipt: ReceiptDetail
  onAdopt: (params: UpdateReceiptParams) => void
}

/** 検算エラーと、2モデルの読み取りが食い違った箇所をまとめて出す */
export const ReceiptIssuePanel = ({ receipt, onAdopt }: Props) => {
  const diffs = receipt.crosscheck?.diffs ?? []
  if (receipt.issues.length === 0 && diffs.length === 0) return null

  return (
    <div className="flex flex-col gap-3">
      {receipt.issues.length > 0 && (
        <section className="flex flex-col gap-1">
          <h3 className="text-sm text-muted-foreground">検算</h3>
          {receipt.issues.map((issue) => (
            <div
              key={`${issue.code}-${issue.message}`}
              className="flex items-start gap-2 rounded border border-gray-700 p-2 text-sm"
            >
              <Badge variant={issue.is_error ? 'destructive' : 'secondary'}>{issue.is_error ? 'エラー' : '警告'}</Badge>
              <span>{issue.message}</span>
            </div>
          ))}
        </section>
      )}

      {diffs.length > 0 && (
        <section className="flex flex-col gap-1">
          <h3 className="text-sm text-muted-foreground">
            AIの読み取り違い（{receipt.crosscheck?.primary_model} / {receipt.crosscheck?.secondary_model}）
          </h3>
          {diffs.map((diff) => {
            const adoptParams = buildAdoptParams(diff.field_name, diff.secondary)
            return (
              <div key={diff.field_name} className="flex flex-col gap-1 rounded border border-gray-700 p-2 text-sm">
                <div className="flex items-center gap-2">
                  {diff.is_critical && <Badge variant="destructive">要確認</Badge>}
                  <span className="text-muted-foreground">{formatFieldLabel(diff.field_name)}</span>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <span>採用中: {diff.primary ?? '（なし）'}</span>
                  <span className="text-muted-foreground">/ もう一方: {diff.secondary ?? '（なし）'}</span>
                  {adoptParams && (
                    <Button size="sm" variant="outline" onClick={() => onAdopt(adoptParams)}>
                      もう一方を採用
                    </Button>
                  )}
                </div>
              </div>
            )
          })}
        </section>
      )}
    </div>
  )
}
